import classnames from 'classnames';
import { forwardRef, type HTMLProps, type ReactNode } from 'react';

import {
  Span,
  type TextVariant,
} from '@/core/tamagoshiv2/components/Typography/Typography';
import { CheckIcon } from '@/core/tamagoshiv2/icons/system/CheckIcon';
import { SIZE, type TamagoshiComponent } from '@/core/tamagoshiv2/interfaces';

import styles from './Checkbox.module.scss';

type Props = TamagoshiComponent & {
  inputClassName?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  /** Event handler for when the user blurs the component */
  onBlur?: HTMLProps<HTMLLabelElement>['onBlur'];
  /** Event handler for when the user changes the input value */
  onChange?: HTMLProps<HTMLInputElement>['onChange'];
  /** Event handler for when the user clicks the component */
  onClick?: HTMLProps<HTMLInputElement>['onClick'];
  size?: `${SIZE.LARGE}` | `${SIZE.MEDIUM}`;
} & (
    | {
        primary?: string;
        primaryClassName?: string;
        secondary?: never;
        secondaryClassName?: never;
      }
    | {
        /** Primary text to use as label for the checkbox */
        primary: string;
        primaryClassName?: string;
        /** Optional decoration to augment the primary text */
        secondary?: ReactNode | string;
        secondaryClassName?: string;
      }
  );

const SIZE_TO_TYPOGRAPHY: Record<NonNullable<Props['size']>, TextVariant> = {
  L: 'body1',
  M: 'body2',
};

/**
 * A controlled checkbox input with label.
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15439-3554
 * @deprecated Use `@/core/tamagoshiTailwind/components/Checkbox/Checkbox` instead
 */
export const Checkbox = forwardRef<HTMLLabelElement, Props>(
  (
    {
      className,
      inputClassName,
      isChecked = false,
      isDisabled,
      onBlur,
      onChange,
      onClick,
      primary,
      primaryClassName,
      secondary,
      secondaryClassName,
      size = 'M',
    },
    root,
  ) => {
    const handleBlur: Props['onBlur'] = (event) => onBlur?.(event);

    const handleChange: Props['onChange'] = (event) => onChange?.(event);

    const handleClick: Props['onClick'] = (event) => onClick?.(event);

    return (
      <label
        className={classnames(
          styles.root,
          isChecked && styles.checked,
          isDisabled && styles.disabled,
          className,
        )}
        onBlur={handleBlur}
        ref={root}
      >
        <span
          className={classnames(styles.checkbox, inputClassName)}
          data-testid="checkbox"
        >
          <input
            aria-disabled={isDisabled}
            checked={isChecked}
            className={styles.input}
            disabled={isDisabled}
            onChange={handleChange}
            onClick={handleClick}
            type="checkbox"
          />
          {isChecked && (
            <CheckIcon
              aria-hidden={false}
              aria-label="Check"
              className={styles.icon}
              role="img"
            />
          )}
        </span>
        {primary && (
          <Span
            className={classnames(styles.primary, primaryClassName)}
            variant={SIZE_TO_TYPOGRAPHY[size]}
          >
            {primary}
          </Span>
        )}
        {secondary && (
          <Span
            className={classnames(styles.secondary, secondaryClassName)}
            variant={SIZE_TO_TYPOGRAPHY[size]}
          >
            <>{secondary}</>
          </Span>
        )}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
